import React from 'react';
import styled from 'styled-components';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';
import SEO, { createBreadcrumbStructuredData } from '../components/SEO';

const AboutContainer = styled.div`
  min-height: calc(100vh - 200px);
  background: ${colors.background.primary};
  padding: ${spacing[8]} ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[12]} ${spacing[6]};
  }
`;

const AboutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: ${typography.fontSizes['4xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[6]} 0;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: ${spacing[8]};
`;

const SectionTitle = styled.h2`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const SectionContent = styled.p`
  font-size: ${typography.fontSizes.lg};
  line-height: 1.7;
  color: ${colors.text.secondary};
  margin: 0 0 ${spacing[4]} 0;
`;

const ComingSoonBadge = styled.div`
  display: inline-block;
  background: ${colors.primary[100]};
  color: ${colors.primary[700]};
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  margin-bottom: ${spacing[6]};
`;

const AboutPage: React.FC = () => {
  return (
    <AboutContainer>
      <SEO
        title="About Event Finder - Our Mission & AI-Powered Event Discovery"
        description="Learn about Event Finder's mission to connect people with amazing experiences worldwide. Our AI-powered platform aggregates events from multiple sources to help you discover conferences, workshops, and networking events."
        keywords="about event finder, event discovery platform, AI event search, event aggregation, conference finder, workshop discovery"
        canonical="https://www.eventfinder.site/about"
        type="article"
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: 'https://www.eventfinder.site/' },
            { name: 'About', url: 'https://www.eventfinder.site/about' }
          ]),
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Event Finder",
            "description": "Learn about Event Finder's mission to connect people with amazing experiences worldwide.",
            "url": "https://www.eventfinder.site/about",
            "mainEntity": {
              "@type": "Organization",
              "name": "Event Finder",
              "description": "AI-powered event discovery platform helping users find conferences, workshops, training sessions, and networking events worldwide.",
              "url": "https://www.eventfinder.site"
            }
          }
        ]}
      />
      <AboutContent>
        <ComingSoonBadge>Page Under Development</ComingSoonBadge>
        <PageTitle>About Event Finder</PageTitle>
        
        <Section>
          <SectionTitle>Our Mission</SectionTitle>
          <SectionContent>
            Event Finder is dedicated to connecting people with amazing experiences worldwide. 
            We believe that the right event at the right time can be transformative, whether it's 
            a local workshop that sparks a new hobby, a conference that advances your career, or 
            a cultural festival that broadens your perspective.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>What We Do</SectionTitle>
          <SectionContent>
            Our platform aggregates events from multiple sources, using advanced search and 
            recommendation algorithms to help you discover events that match your interests, 
            location, and schedule. From intimate workshops to large-scale conferences, from 
            virtual webinars to outdoor festivals, we curate a diverse range of experiences.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Why Choose Event Finder</SectionTitle>
          <SectionContent>
            We understand that finding the perfect event shouldn't be a chore. That's why we've 
            built intelligent search capabilities, personalized recommendations, and intuitive 
            browsing tools. Our calendar integration, map views, and detailed event information 
            help you make informed decisions about how to spend your valuable time.
          </SectionContent>
        </Section>
      </AboutContent>
    </AboutContainer>
  );
};

export default AboutPage;